/* eslint-disable */
import {
  // Table and Row Management
  SET_SELECTED_ROW,
  SET_API_DATA,

  // Loading States
  SET_LOADING,

  // Drawer Management
  SET_DRAWER_OPEN,
  SET_REQUEST_ID,

  // Filter Management
  SET_CURRENT_FILTERS,
  CLEAR_ALL_FILTERS,
  DELETE_FILTER,

  // State Reset
  RESET_REQUEST_STATE,

  // Form Management
  SET_FILTER_FORM_DATA,
  UPDATE_FILTER_FIELD,

  // Additional Actions
  SET_CURRENT_ROW,
  SET_API_ERROR,
  CLEAR_API_ERROR,

  // Root Level Actions
  SET_CURRENT_STATUS_FILTER,
  SET_CURRENT_RENDER_COUNT,
  SET_REQUEST_API_URL,
  SET_INSTRUMENT_FILTER,
} from '../constants/RequestTypes';

/**
 * Initial state for the request module
 * Contains all state related to request management, filtering, and UI interactions
 */
const initialState = {
  // Table Management
  selectedRow: {},
  apiData: { recordsList: [] },
  currentRow: null,

  // Loading States
  loading: false,
  apiError: null,

  // Drawer Management
  drawerOpen: false,
  requestId: '',

  // Filter Management
  currentFilters: {},
  filterFormData: {
    requestedBy: null,
    instrumentDescription: '',
    system: '',
    status: [],
    manufacturer: '',
    instrumentOwner: null,
    modelNumber: '',
    serialNumber: '',
    assetClass: '',
    assetType: '',
    serviceProvider: '',
    site: null,
    building: '',
    floorLevel: '',
    roomSpace: '',
    department: [],
    gxpStatus: [],
    businessCriticality: [],
    requestType: [],
    costCenter: '',
    purchaseCost: '',
    financialId: '',
    installDate: null,
    endOfWarrantyDate: null,
    pms: '',
    qualifications: '',
    calibrations: '',
    repairs: '',
    exceptionDetails: '',
  },

  // UI State
  renderCount: 0,
  tableRef: null,

  // Root Level State
  currentStatusFilter: { code: 'ALL', currentStatusList: [] },
  currentRenderCount: 1,
  requestApiUrl: '',
  instrumentFilter: [],

  // Pagination and Sorting
  pageSize: 10,
  pageNumber: 1,
  sortBy: [],

  // Additional State
  lastUpdated: null,
  isDirty: false,
};

/**
 * Request Reducer
 * Handles all state changes related to request management, filtering, and UI interactions
 *
 * @param {Object} state - Current state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} New state
 */
const requestReducer = (state = initialState, action) => {
  switch (action.type) {
    // ===== TABLE AND ROW MANAGEMENT =====
    case SET_SELECTED_ROW:
      return {
        ...state,
        selectedRow: action.payload,
      };

    case SET_API_DATA:
      return {
        ...state,
        apiData: action.payload,
        lastUpdated: new Date().toISOString(),
        apiError: null,
      };

    // ===== LOADING STATES =====
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    // ===== DRAWER MANAGEMENT =====
    case SET_DRAWER_OPEN:
      return {
        ...state,
        drawerOpen: action.payload,
      };

    case SET_REQUEST_ID:
      return {
        ...state,
        requestId: action.payload,
      };

    // ===== FILTER MANAGEMENT =====
    case SET_CURRENT_FILTERS:
      return {
        ...state,
        currentFilters: action.payload || {},
        isDirty: true,
        lastUpdated: new Date().toISOString(),
      };

    case CLEAR_ALL_FILTERS:
      console.log('Action - clearAllFilters');
      return {
        ...state,
        currentFilters: {},
        filterFormData: {
          status: [],
          requestType: [],
          priority: [],
          department: [],
          building: [],
          assignedTo: [],
        },
        isDirty: false,
        lastUpdated: new Date().toISOString(),
      };

    case DELETE_FILTER:
      const { filterKey, filterValue } = action.payload;
      const updatedFilters = { ...state.currentFilters };

      if (filterKey in updatedFilters) {
        if (Array.isArray(updatedFilters[filterKey]) && filterValue) {
          updatedFilters[filterKey] = updatedFilters[filterKey].filter(
            (item) => JSON.stringify(item) !== JSON.stringify(filterValue),
          );
          if (updatedFilters[filterKey].length === 0) {
            delete updatedFilters[filterKey];
          }
        } else {
          delete updatedFilters[filterKey];
        }
      }

      return {
        ...state,
        currentFilters: updatedFilters,
        isDirty: true,
        lastUpdated: new Date().toISOString(),
      };

    // ===== FORM MANAGEMENT =====
    case SET_FILTER_FORM_DATA:
      return {
        ...state,
        filterFormData: action.payload,
        isDirty: true,
        lastUpdated: new Date().toISOString(),
      };

    case UPDATE_FILTER_FIELD:
      const { field, value } = action.payload;
      return {
        ...state,
        filterFormData: {
          ...state.filterFormData,
          [field]: value,
        },
        isDirty: true,
        lastUpdated: new Date().toISOString(),
      };

    // ===== ADDITIONAL STATE MANAGEMENT =====
    case SET_CURRENT_ROW:
      return {
        ...state,
        currentRow: action.payload,
      };

    // ===== ERROR MANAGEMENT =====
    case SET_API_ERROR:
      return {
        ...state,
        apiError: action.payload,
        loading: false,
      };

    case CLEAR_API_ERROR:
      return {
        ...state,
        apiError: null,
      };

    // ===== ROOT LEVEL STATE MANAGEMENT =====
    case SET_CURRENT_STATUS_FILTER:
      return {
        ...state,
        currentStatusFilter: action.payload,
        isDirty: true,
        lastUpdated: new Date().toISOString(),
      };

    case SET_CURRENT_RENDER_COUNT:
      return {
        ...state,
        currentRenderCount: action.payload,
      };

    case SET_REQUEST_API_URL:
      return {
        ...state,
        requestApiUrl: action.payload,
      };

    case SET_INSTRUMENT_FILTER:
      return {
        ...state,
        instrumentFilter: action.payload,
      };

    // ===== STATE RESET =====
    case RESET_REQUEST_STATE:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

// Export the reducer as default
export default requestReducer;

// Export initial state for testing purposes
export { initialState };

/**
 * Request Reducer Export
 *
 * This reducer manages the complete state of the request module including:
 * - Table and row selection management
 * - API data and loading states
 * - Filter and search functionality
 * - Drawer and modal states
 * - Form data management
 * - Error handling
 *
 * Usage:
 * import requestReducer from './reducers/Request';
 *
 * State Structure:
 * {
 *   selectedRow: {},           // Currently selected table row
 *   apiData: {},              // API response data
 *   currentRow: null,         // Current active row
 *   loading: false,           // Loading state
 *   apiError: null,          // API error state
 *   drawerOpen: false,        // Drawer open/closed state
 *   requestId: '',           // Current request ID
 *   currentFilters: {},       // Applied filters
 *   filterFormData: {},       // Form data for filters
 *   renderCount: 0,          // Render counter
 *   tableRef: null,          // Table reference
 *   pageSize: 10,            // Pagination size
 *   pageNumber: 1,           // Current page
 *   sortBy: [],              // Sort configuration
 *   lastUpdated: null,       // Last update timestamp
 *   isDirty: false           // State change indicator
 * }
 */
